import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import { type FC, useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useSupplierSearch } from "../../hooks/index.ts"
import type { GoodsReceiptFormValues } from "../../schemas.ts"
import { goodsReceiptFormSchema } from "../../schemas.ts"
import { getGoodsReceiptDefaultFormValues } from "../../utils.ts"

import { GoodsReceiptForm } from "./GoodsReceiptForm.tsx"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GoodsReceipt, Order } from "@/graphql/codegen/graphql"

interface GoodsReceiptFormDialogProps {
  onClose: () => void
  onSubmit: (formValues: GoodsReceiptFormValues) => void
  hasPurchaseOrder?: boolean
  isLoading?: boolean
  goodsReceipt?: GoodsReceipt
  selectedPurchaseOrders?: Order[]
}

export const GoodsReceiptFormDialog: FC<GoodsReceiptFormDialogProps> = ({
  onClose,
  onSubmit,
  hasPurchaseOrder = false,
  isLoading = false,
  goodsReceipt = null,
  selectedPurchaseOrders = [],
}) => {
  const isCreateMode = !goodsReceipt
  const { supplierOptions, onSearch: onSupplierSearch, isFetching: isSupplierListFetching } = useSupplierSearch()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)

  const methods = useForm<GoodsReceiptFormValues>({
    resolver: zodResolver(goodsReceiptFormSchema),
    defaultValues: getGoodsReceiptDefaultFormValues(goodsReceipt, selectedPurchaseOrders),
    mode: "all",
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
  } = methods

  const canSubmit = isValid && (isDirty || (isCreateMode && selectedPurchaseOrders.length > 0)) && !isLoading

  // Check if GR has purchase orders (either existing or newly selected)
  const hasExistingPurchaseOrders = goodsReceipt?.orders && goodsReceipt.orders.length > 0
  const hasSelectedPurchaseOrders = selectedPurchaseOrders.length > 0
  const isDeliveryLocationDisabled = hasExistingPurchaseOrders || hasSelectedPurchaseOrders

  const handleClose = useCallback(() => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleCancelConfirm = () => {
    reset()
    onClose()
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCancelConfirm} />
  ) : (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth data-testid='goods-receipt-form-dialog'>
      <DialogHeader
        title={isCreateMode ? "New Advanced Goods Receipt" : "Edit Advanced Goods Receipt"}
        onClose={handleClose}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className='pt-4'>
            <GoodsReceiptForm
              hasPurchaseOrder={hasPurchaseOrder}
              onSupplierSearch={onSupplierSearch}
              supplierOptions={supplierOptions}
              isLoadingSuppliers={isSupplierListFetching}
              isDeliveryLocationDisabled={isDeliveryLocationDisabled}
            />
          </DialogContent>

          <DialogActions className='px-6 pb-6'>
            <Button appearance='contained' onClick={handleClose} disabled={isLoading} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              type='submit'
              appearance='contained'
              disabled={!canSubmit}
              loading={isLoading}
              data-testid='save-button'
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
